import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCcw, AlertTriangle, CheckCircle, XCircle, Clock, Send, LogOut } from 'lucide-react';
import supportAxios from '../../utils/supportAxios';

const STATUS_COLORS = {
    pending: 'bg-yellow-100 text-yellow-800',
    retried: 'bg-yellow-100 text-yellow-800',
    sent:    'bg-green-100 text-green-800',
    failed:  'bg-red-100 text-red-800',
};

const SupportDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [failedLogs, setFailedLogs] = useState([]);
    const [activityLogs, setActivityLogs] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const [busyAll, setBusyAll] = useState(false);
    const [filter, setFilter] = useState('failed');

    const load = useCallback(async () => {
        try {
            const [summaryRes, logsRes, activityRes] = await Promise.all([
                supportAxios.get('/support/api/dashboard/summary'),
                supportAxios.get(`/support/api/dashboard/logs?status=${filter}&limit=50`),
                supportAxios.get('/support/api/dashboard/activity?limit=80'),
            ]);
            setSummary(summaryRes.data.summary || {});
            setFailedLogs(logsRes.data.rows || []);
            setActivityLogs(activityRes.data.logs || []);
        } catch (err) {
            console.error('Failed to load dashboard data:', err);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        const token = localStorage.getItem('supportToken');
        if (!token) {
            navigate('/support/login');
            return;
        }
        load();
    }, [load, navigate]);

    const retryEmail = async (id) => {
        setBusyId(id);
        try {
            await supportAxios.post('/support/api/dashboard/retry', { id });
            await load();
        } finally {
            setBusyId(null);
        }
    };

    const retryAll = async () => {
        setBusyAll(true);
        try {
            await supportAxios.post('/support/api/dashboard/retry-all');
            await load();
        } finally {
            setBusyAll(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('supportToken');
        localStorage.removeItem('supportUser');
        navigate('/support/login');
    };

    const summaryCards = summary ? [
        { label: 'Total',      value: summary.total || 0,            icon: AlertTriangle, color: 'text-gray-700' },
        { label: 'Pending',    value: summary.pending || 0,          icon: Clock,         color: 'text-yellow-600' },
        { label: 'Sent',       value: summary.sent || 0,             icon: CheckCircle,   color: 'text-green-600' },
        { label: 'Failed',     value: summary.failed || 0,           icon: XCircle,       color: 'text-red-600' },
        { label: 'Permanent',  value: summary.permanent_failures || 0, icon: AlertTriangle, color: 'text-red-800' },
    ] : [];

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <RefreshCcw className="w-6 h-6 text-purple-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-xs">S</span>
                    </div>
                    <h1 className="text-lg font-semibold text-gray-800">Support Dashboard</h1>
                </div>
                <button onClick={logout} className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1">
                    <LogOut className="w-4 h-4" /> Logout
                </button>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {summaryCards.map((card) => (
                        <div key={card.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                            <div className="flex items-center gap-2 mb-1">
                                <card.icon className={`w-4 h-4 ${card.color}`} />
                                <span className="text-xs text-gray-500">{card.label}</span>
                            </div>
                            <p className="text-2xl font-bold text-gray-800">{card.value}</p>
                        </div>
                    ))}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={retryAll}
                        disabled={busyAll}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-60 transition"
                    >
                        <RefreshCcw className={`w-4 h-4 ${busyAll ? 'animate-spin' : ''}`} />
                        Retry All Pending
                    </button>
                    <button
                        onClick={load}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                    >
                        <RefreshCcw className="w-4 h-4" /> Refresh
                    </button>
                </div>

                {/* Failed Emails */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="font-semibold text-gray-800">Email Error Logs</h2>
                        <select
                            value={filter}
                            onChange={(e) => { setFilter(e.target.value); setLoading(true); }}
                            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-purple-300 outline-none"
                        >
                            <option value="failed">Failed</option>
                            <option value="pending">Pending</option>
                            <option value="sent">Sent</option>
                            <option value="retried">Retried</option>
                            <option value="">All</option>
                        </select>
                    </div>
                    {failedLogs.length === 0 ? (
                        <p className="px-6 py-8 text-gray-400 text-sm">No records found.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-600">
                                    <tr>
                                        <th className="px-4 py-3 text-left font-medium">ID</th>
                                        <th className="px-4 py-3 text-left font-medium">Recipient</th>
                                        <th className="px-4 py-3 text-left font-medium">Subject</th>
                                        <th className="px-4 py-3 text-left font-medium">Source</th>
                                        <th className="px-4 py-3 text-left font-medium">Attempts</th>
                                        <th className="px-4 py-3 text-left font-medium">Status</th>
                                        <th className="px-4 py-3 text-left font-medium">Error</th>
                                        <th className="px-4 py-3 text-left font-medium">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {failedLogs.map((row) => (
                                        <tr key={row.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 font-mono text-gray-500">{row.id}</td>
                                            <td className="px-4 py-3 text-gray-800">{row.recipient || "-"}</td>
                                            <td className="px-4 py-3 text-gray-800 max-w-[180px] truncate" title={row.subject}>
                                                {row.subject || "-"}
                                            </td>
                                            <td className="px-4 py-3 text-gray-500 text-xs">{row.source || "-"}</td>
                                            <td className="px-4 py-3 text-gray-600">{row.attempts}/{row.max_attempts}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[row.status] || 'bg-gray-100 text-gray-600'}`}>
                                                    {row.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-red-600 text-xs max-w-[180px] truncate" title={row.last_error_message || row.error_message}>
                                                {row.last_error_message || row.error_message || "-"}
                                            </td>
                                            <td className="px-4 py-3">
                                                {row.status !== 'sent' && row.attempts < row.max_attempts && (
                                                    <button
                                                        onClick={() => retryEmail(row.id)}
                                                        disabled={busyId === row.id}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium hover:bg-purple-200 disabled:opacity-50 transition"
                                                    >
                                                        <Send className="w-3 h-3" />
                                                        {busyId === row.id ? 'Retrying…' : 'Retry'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Activity Logs */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h2 className="font-semibold text-gray-800">Recent Activity Logs</h2>
                    </div>
                    <div className="max-h-96 overflow-y-auto px-6 py-3 text-xs font-mono text-gray-700 space-y-1">
                        {activityLogs.length === 0 && <p className="text-gray-400">No activity logs available.</p>}
                        {activityLogs.map((log, i) => (
                            <div key={i} className="flex gap-2">
                                {log.timestamp && <span className="text-gray-400 shrink-0">{log.timestamp}</span>}
                                {log.level && (
                                    <span className={`shrink-0 font-semibold ${log.level === 'ERROR' ? 'text-red-600' : log.level === 'WARN' ? 'text-yellow-600' : 'text-gray-500'}`}>
                                        [{log.level}]
                                    </span>
                                )}
                                <span>{log.message}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SupportDashboard;